// ============================================================================
// assistantSources.ts — разбор блока «Источники ответа» личного ассистента.
//
// Формат — SSoT сервера (internal/assistantsources): невидимый HTML-комментарий
// в конце сообщения с base64url(JSON). Клиент НЕ доверяет маркеру из чужих
// сообщений: блок показывается только для ответа бота-ассистента, а сам маркер
// вырезается из отображаемого текста в любом случае.
// ============================================================================

export type AnswerSourceKind = 'record' | 'table' | 'file' | 'url' | 'tool';

export interface AnswerSource {
  kind: AnswerSourceKind;
  label: string;
  detail?: string;
  tool?: string;
  table?: string;
  entityType?: string;
  entityCode?: string;
  recordId?: string;
  fileId?: string;
  url?: string;
}

export interface ParsedAssistantText {
  /** Текст без служебного маркера. */
  text: string;
  /** null — маркера нет (не ответ ассистента); [] — маркер есть, данных нет. */
  sources: AnswerSource[] | null;
  /** Был ли в сообщении серверный маркер (даже если он не разобран). */
  marker: boolean;
}

const MARKER_AT_END = /\n\n<!--hydra:sources:v1:([A-Za-z0-9_-]+)-->$/;
const MARKER_ANY = /<!--hydra:sources:v1:[A-Za-z0-9_-]+-->/g;
const LEGACY_NOTE = /\n{2}Источники ответа:\s*([^\n]+?)\.\s*$/u;
const KINDS: readonly AnswerSourceKind[] = ['record', 'table', 'file', 'url', 'tool'];

function trimText(value: unknown, limit = 160): string {
  if (typeof value !== 'string') return '';
  const text = value.replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

function normalizeSource(raw: unknown): AnswerSource | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const kind = typeof item.kind === 'string' && KINDS.includes(item.kind as AnswerSourceKind)
    ? item.kind as AnswerSourceKind
    : 'tool';
  const label = trimText(item.label);
  if (!label) return null;
  return {
    kind,
    label,
    detail: trimText(item.detail) || undefined,
    tool: trimText(item.tool) || undefined,
    table: trimText(item.table) || undefined,
    entityType: trimText(item.entity_type ?? item.entityType) || undefined,
    entityCode: trimText(item.entity_code ?? item.entityCode) || undefined,
    recordId: trimText(item.record_id ?? item.recordId) || undefined,
    fileId: trimText(item.file_id ?? item.fileId) || undefined,
    url: trimText(item.url, 2000) || undefined,
  };
}

function decodeSources(token: string): AnswerSource[] | null {
  try {
    const padded = token.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(token.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder('utf-8').decode(bytes));
    if (!Array.isArray(parsed)) return null;
    return parsed
      .slice(0, 30)
      .map(normalizeSource)
      .filter((item): item is AnswerSource => item !== null);
  } catch {
    return null;
  }
}

/** Вырезает служебные маркеры (для превью/цитат/поиска/копирования). */
export function stripAssistantSources(text: string): string {
  if (!text) return '';
  if (!text.includes('<!--hydra:sources:')) return text;
  return text.replace(MARKER_ANY, '').replace(/\s+$/u, '');
}

/**
 * Разбор ответа ассистента: маркер в конце → структурированные источники.
 * Старые ответы (до блока) содержат строку «Источники ответа: …» — она также
 * разворачивается в источники-инструменты (данные реальные, без ссылок).
 */
export function parseAssistantSources(text: string): ParsedAssistantText {
  if (!text) return { text: '', sources: null, marker: false };

  const markerMatch = MARKER_AT_END.exec(text);
  if (markerMatch) {
    const clean = text.slice(0, markerMatch.index).replace(/\s+$/u, '');
    const decoded = decodeSources(markerMatch[1]);
    return { text: clean, sources: decoded ?? [], marker: true };
  }

  const legacyMatch = LEGACY_NOTE.exec(text);
  if (legacyMatch) {
    const clean = text.slice(0, legacyMatch.index).replace(/\s+$/u, '');
    const labels = legacyMatch[1]
      .split(',')
      .map((label) => trimText(label))
      .filter(Boolean)
      .slice(0, 4);
    return {
      text: clean,
      sources: labels.map((label) => ({ kind: 'tool', label })),
      marker: true,
    };
  }

  return { text: stripAssistantSources(text), sources: null, marker: false };
}
